import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { topicsAPI, trendsAPI } from '../services/apiEndpoints';
import { useContentVertex } from '../contexts/ContentVertexContext';
import echo from '../services/echo';
import QuickPost from './QuickPost';
import DeleteModal from './DeleteModal';
import BulkDeleteModal from './BulkDeleteModal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
    MagnifyingGlassIcon,
    PlusIcon,
    ReloadIcon,
    TrashIcon,
    EyeOpenIcon,
    MixerHorizontalIcon,
    CheckIcon,
    Cross2Icon,
    FileTextIcon,
    DashboardIcon,
    BarChartIcon,
    GearIcon,
    ExitIcon,
    LightningBoltIcon,
    ActivityLogIcon,
    GlobeIcon,
    PaperPlaneIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    RocketIcon
} from '@radix-ui/react-icons';
import { Separator } from './ui/separator';
import { Avatar } from './ui/avatar';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const TopicQueue = () => {
    const { onGenerateClick, niche } = useContentVertex();

    const NICHE_EMPTY = {
        affiliate: {
            headline: 'Your affiliate pipeline is empty',
            body: 'Your next review article could be earning commissions by tomorrow.',
        },
        blog: {
            headline: 'Your content pipeline is empty',
            body: "Your competitors published 3 articles today. Every day you wait is ground you can't get back.",
        },
        news: {
            headline: 'No stories queued',
            body: "Breaking news doesn't wait. Be first with AI-generated coverage.",
        },
        ecommerce: {
            headline: 'Your product content pipeline is empty',
            body: 'Every product page without SEO content is revenue you leave on the table.',
        },
        agency: {
            headline: 'No client content queued',
            body: "Your clients expect fresh content. ContentVertex lets you deliver faster than they ask.",
        },
    };
    const emptyMsg = NICHE_EMPTY[niche] || {
        headline: 'Your content pipeline is empty',
        body: "Your competitors are publishing 3–5 articles per day. Every day without content is ground you can't get back.",
    };
    const [topics, setTopics] = useState([]);
    const [filteredTopics, setFilteredTopics] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [trendingTopics, setTrendingTopics] = useState([]);
    const [showAddPage, setShowAddPage] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [selectedTopicForDelete, setSelectedTopicForDelete] = useState(null);
    const [showDraftPreviewDialog, setShowDraftPreviewDialog] = useState(false);
    const [draftUrlToOpen, setDraftUrlToOpen] = useState('');
    const [statusFilter, setStatusFilter] = useState([]);
    const [siteFilter, setSiteFilter] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [retryingId, setRetryingId] = useState(null);
    const [publishingId, setPublishingId] = useState(null);
    const [retryToast, setRetryToast] = useState(null);
    const [bulkPublishing, setBulkPublishing] = useState(false);
    const [bulkPublishToast, setBulkPublishToast] = useState(null);
    const [publishWinToast, setPublishWinToast] = useState(null);
    const [pollingId, setPollingId] = useState(null); // topic id being polled
    const pollIntervalRef = useRef(null);
    const itemsPerPage = 20;

    const navigate = useNavigate();
    const location = useLocation();
    const isActive = (path) => location.pathname === path;

    useEffect(() => {
        loadTopics();
        loadTrendingTopics();
    }, []);

    const loadTrendingTopics = async () => {
        try {
            const res = await trendsAPI.discoverTrending('content marketing', '2840', false, false);
            const data = res.data?.trending_topics || res.data?.topics || [];
            setTrendingTopics(data.slice(0, 3));
        } catch (e) {
            // non-critical
        }
    };

    useEffect(() => {
        applyFilters();
    }, [topics, statusFilter, siteFilter, searchQuery]);

    useEffect(() => {
        const channel = echo.channel('article-progress');

        channel.listen('.progress-updated', (data) => {
            setTopics(prevTopics => {
                const updated = prevTopics.map(topic =>
                    topic.id === data.topicId
                        ? { ...topic, progress: data.progress, status: data.status }
                        : topic
                );
                // Fire win toast when an article transitions to published
                if (data.status === 'published') {
                    const topic = prevTopics.find(t => t.id === data.topicId);
                    if (topic) {
                        setPublishWinToast({
                            title: topic.generated_title || topic.topic_title,
                            url: topic.wordpress_url || null,
                            score: topic.seo_score || null,
                        });
                        setTimeout(() => setPublishWinToast(null), 6000);
                    }
                }
                return updated;
            });
        });

        return () => {
            channel.stopListening('.progress-updated');
            echo.leaveChannel('article-progress');
        };
    }, []);

    const loadTopics = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res = await topicsAPI.getTopics();
            const topicsData = res.data.data || res.data.topics || res.data || [];
            const topicsArray = Array.isArray(topicsData) ? topicsData : [];
            setTopics(topicsArray);
        } catch {
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...topics];

        if (searchQuery) {
            filtered = filtered.filter(t =>
                (t.generated_title || t.topic_title || '').toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (statusFilter.length > 0) {
            filtered = filtered.filter(t => statusFilter.includes(t.status));
        }

        if (siteFilter.length > 0) {
            filtered = filtered.filter(t =>
                siteFilter.includes(t.word_press_site?.site_name || t.wordpress_site?.name)
            );
        }

        setFilteredTopics(filtered);

        const newTotalPages = Math.ceil(filtered.length / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        } else if (filtered.length === 0) {
            setCurrentPage(1);
        }
    };

    const toggleSelectAll = () => {
        if (selectedTopics.length === paginatedTopics.length) {
            setSelectedTopics([]);
        } else {
            setSelectedTopics(paginatedTopics.map(t => t.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedTopics(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const confirmBulkDelete = async () => {
        if (selectedTopics.length === 0) return;

        try {
            await topicsAPI.deleteTopics(selectedTopics);
            setSelectedTopics([]);
            setShowBulkDeleteModal(false);
            loadTopics();
        } catch {
        }
    };

    const openBulkDeleteModal = () => {
        if (selectedTopics.length === 0) return;
        setShowBulkDeleteModal(true);
    };

    const handleBulkGenerate = async () => {
        if (selectedTopics.length === 0) return;

        try {
            await topicsAPI.generateTopics(selectedTopics);
            setSelectedTopics([]);
            loadTopics();
        } catch {
        }
    };

    const PUBLISHABLE_STATUSES = ['draft', 'generated', 'needs_review'];

    const handleBulkPublish = async () => {
        const publishableIds = selectedTopics.filter(id => {
            const t = topics.find(tp => tp.id === id);
            return t && PUBLISHABLE_STATUSES.includes(t.status);
        });
        if (publishableIds.length === 0) return;

        setBulkPublishing(true);
        try {
            const res = await topicsAPI.bulkPublishTopics(publishableIds);
            const { published, failed } = res.data;
            setBulkPublishToast({ published, failed });
            setTimeout(() => setBulkPublishToast(null), 4000);
            setSelectedTopics([]);
            loadTopics();
        } catch {
            setBulkPublishToast({ published: 0, failed: publishableIds.length });
            setTimeout(() => setBulkPublishToast(null), 4000);
        } finally {
            setBulkPublishing(false);
        }
    };

    const handlePublishAllReady = async () => {
        const readyIds = topics
            .filter(t => PUBLISHABLE_STATUSES.includes(t.status))
            .map(t => t.id);
        if (readyIds.length === 0) return;

        setBulkPublishing(true);
        try {
            const res = await topicsAPI.bulkPublishTopics(readyIds);
            const { published, failed } = res.data;
            setBulkPublishToast({ published, failed });
            setTimeout(() => setBulkPublishToast(null), 4000);
            loadTopics();
        } catch {
        } finally {
            setBulkPublishing(false);
        }
    };

    const handleDelete = async (id) => {
        const topic = topics.find(t => t.id === id);
        if (topic) {
            setSelectedTopicForDelete(topic);
            setShowDeleteModal(true);
        }
    };

    const handleDeleteSuccess = (message, deletedFrom) => {
        setTopics(topics.filter(t => t.id !== selectedTopicForDelete.id));
        alert(message);
        setShowDeleteModal(false);
        setSelectedTopicForDelete(null);
        loadTopics();
    };

    const handlePublishLive = async (id) => {
        if (!window.confirm('Publish this draft live to WordPress?')) return;
        setPublishingId(id);
        try {
            await topicsAPI.publishTopic(id, 'publish');
            setTopics(prev => prev.map(t => t.id === id ? { ...t, status: 'published' } : t));
        } catch (e) {
            alert('Failed to publish: ' + (e.response?.data?.message || e.message));
            loadTopics();
        } finally {
            setPublishingId(null);
        }
    };

    const handleGenerate = async (id) => {
        try {
            await topicsAPI.generateTopic(id);
            loadTopics();
        } catch {
        }
    };

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        setPollingId(null);
    };

    const startPolling = (id) => {
        stopPolling();
        setPollingId(id);
        pollIntervalRef.current = setInterval(async () => {
            try {
                const res = await topicsAPI.getTopic(id);
                const topic = res.data?.topic || res.data;
                if (!topic) return;

                setTopics(prev => prev.map(t => t.id === id ? { ...t, ...topic } : t));

                // Stop polling once WebSocket can take over or job is terminal
                const terminal = ['processing', 'draft', 'published', 'failed', 'needs_review', 'generated'];
                if (terminal.includes(topic.status)) {
                    stopPolling();
                    if (topic.status === 'failed') {
                        setRetryToast({ message: 'Generation failed again. Check error.', error: true });
                        setTimeout(() => setRetryToast(null), 5000);
                    }
                }
            } catch {
            }
        }, 2500);
    };

    const handleRetry = async (id) => {
        setRetryingId(id);
        // Optimistically update row immediately
        setTopics(prev => prev.map(t =>
            t.id === id ? { ...t, status: 'queued', progress: 0, error_message: null } : t
        ));
        try {
            await topicsAPI.retryTopic(id);
            setRetryToast({ message: 'Retrying… watching for progress' });
            setTimeout(() => setRetryToast(null), 3000);
            startPolling(id);
        } catch {
            setTopics(prev => prev.map(t => t.id === id ? { ...t, status: 'failed' } : t));
            setRetryToast({ message: 'Failed to retry. Please try again.', error: true });
            setTimeout(() => setRetryToast(null), 4000);
        } finally {
            setRetryingId(null);
        }
    };

    // Clean up poll on unmount
    useEffect(() => () => stopPolling(), []);

    const handleViewDraft = (url) => {
        const hideDraftWarning = localStorage.getItem('hideDraftPreviewWarning');
        if (hideDraftWarning === 'true') {
            window.open(url, '_blank');
        } else {
            setDraftUrlToOpen(url);
            setShowDraftPreviewDialog(true);
        }
    };

    const handleDraftPreviewConfirm = (dontShowAgain) => {
        if (dontShowAgain) {
            localStorage.setItem('hideDraftPreviewWarning', 'true');
        }
        window.open(draftUrlToOpen, '_blank');
        setShowDraftPreviewDialog(false);
        setDraftUrlToOpen('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatWordCount = (count) => {
        if (!count) return '—';
        if (count >= 1000) return (count / 1000).toFixed(1) + 'k';
        return String(count);
    };

    const renderSEOScore = (score, breakdown) => {
        if (score == null) return <span className="text-zinc-600 text-xs">—</span>;
        const color = score >= 90 ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' :
            score >= 80 ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' :
                score >= 70 ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' :
                    'text-red-400 bg-red-400/10 border-red-400/30';

        if (!breakdown || breakdown.length === 0) {
            return (
                <span className={`px-2 py-1 rounded-full border text-xs font-medium ${color}`}>
                    {score}
                </span>
            );
        }

        return (
            <div style={{ position: 'relative', display: 'inline-block' }} className="seo-score-wrap">
                <span className={`px-2 py-1 rounded-full border text-xs font-medium cursor-help ${color}`}>
                    {score}
                </span>
                <div style={{
                    position: 'absolute', bottom: '130%', right: 0,
                    width: '240px', background: '#18181b', border: '1px solid #3f3f46',
                    borderRadius: '10px', padding: '10px 12px', zIndex: 9999,
                    display: 'none', flexDirection: 'column', gap: '6px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }} className="seo-tooltip">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #3f3f46', paddingBottom: '6px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#e4e4e7' }}>SEO Breakdown</span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: score >= 90 ? '#34d399' : score >= 70 ? '#facc15' : '#f87171' }}>{score}/100</span>
                    </div>
                    {breakdown.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                                <span style={{ color: item.pass ? '#34d399' : '#f87171', fontSize: '10px', flexShrink: 0 }}>{item.pass ? '✓' : '✗'}</span>
                                <span style={{ fontSize: '11px', color: '#a1a1aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.signal}</span>
                            </div>
                            <span style={{ fontSize: '11px', color: '#52525b', flexShrink: 0 }}>{item.score}/{item.max}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getProgressLabel = (progress) => {
        if (progress === 0)  return 'Starting up…';
        if (progress < 20)  return 'Researching keyword…';
        if (progress < 40)  return 'Building outline…';
        if (progress < 60)  return 'Writing sections…';
        if (progress < 80)  return 'Finalising article…';
        if (progress < 100) return 'Publishing…';
        return 'Done';
    };

    const renderStatusProgress = (topic) => {
        if (topic.status === 'processing' || (topic.status === 'queued' && pollingId === topic.id && topic.progress > 0)) {
            const pct = topic.progress || 0;
            return (
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all duration-500"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="text-xs text-zinc-400 shrink-0">{pct}%</span>
                    </div>
                    <span className="text-xs text-zinc-500">{getProgressLabel(pct)}</span>
                </div>
            );
        } else if (topic.status === 'draft' || topic.status === 'published' || topic.status === 'needs_review') {
            return (
                <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
                    <CheckIcon className="h-3.5 w-3.5" />
                    Finished
                </span>
            );
        } else if (topic.status === 'failed') {
            return (
                <span className="flex items-center gap-1.5 text-red-400 text-sm" title={topic.error_message || 'Generation failed'}>
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    Error
                </span>
            );
        } else if (topic.status === 'queued') {
            return (
                <div className="flex flex-col gap-0.5">
                    <span className="flex items-center gap-1.5 text-orange-400 text-sm">
                        <ReloadIcon className="h-3 w-3 animate-spin" />
                        In queue…
                    </span>
                    <span className="text-xs text-zinc-600">Waiting for worker</span>
                </div>
            );
        } else {
            return (
                <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                    <ClockIcon className="h-3 w-3" />
                    Queued
                </span>
            );
        }
    };

    const renderStatus = (status, topicId) => {
        if (publishingId === topicId) {
            return (
                <span className="px-2.5 py-1 rounded-full border text-xs font-medium text-green-400 bg-green-400/10 border-green-400/30 inline-flex items-center gap-1">
                    Publishing
                    <span className="inline-flex gap-0.5">
                        <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                        <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                    </span>
                </span>
            );
        }

        const statusMap = {
            'draft': { label: 'Draft', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
            'published': { label: 'Published', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' },
            'needs_review': { label: 'Review', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30' },
            'scheduled': { label: 'Scheduled', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' },
            'processing': { label: 'Processing', color: 'text-violet-400 bg-violet-400/10 border-violet-400/30' },
            'pending': { label: 'Pending', color: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/30' },
            'queued': { label: 'Queued', color: 'text-orange-400 bg-orange-400/10 border-orange-400/30' },
            'failed': { label: 'Failed', color: 'text-red-400 bg-red-400/10 border-red-400/30' }
        };

        const { label, color } = statusMap[status] || statusMap['pending'];

        return (
            <span className={`px-2.5 py-1 rounded-full border text-xs font-medium ${color}`}>
                {label}
            </span>
        );
    };

    const totalPages = Math.ceil(filteredTopics.length / itemsPerPage);
    const paginatedTopics = filteredTopics.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const uniqueSites = [...new Set(topics.map(t =>
        t.word_press_site?.site_name || t.wordpress_site?.name || 'No Site'
    ))];

    return (
        <div className="text-zinc-50">
            {/* Retry toast */}
            {retryToast && (
                <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-xl text-sm font-medium animate-fade-in-up whitespace-nowrap ${
                    retryToast.error
                        ? 'bg-red-500/10 border-red-500/30 text-red-300'
                        : 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                }`}>
                    <ReloadIcon className={`w-4 h-4 shrink-0 ${retryToast.error ? '' : 'animate-spin'}`} />
                    {retryToast.message}
                </div>
            )}

            {/* Publish win toast */}
            {publishWinToast && (
                <div className="fixed top-6 right-6 z-50 w-80 rounded-xl border border-emerald-500/30 bg-zinc-950/95 shadow-2xl shadow-emerald-500/10 backdrop-blur-xl overflow-hidden animate-fade-in-up">
                    <div className="h-0.5 bg-gradient-to-r from-emerald-500 to-teal-400" />
                    <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-lg leading-none">🎉</span>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-emerald-400 leading-tight">Article is live!</p>
                                    <p className="text-xs text-zinc-300 mt-0.5 leading-snug line-clamp-2">{publishWinToast.title}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setPublishWinToast(null)}
                                className="text-zinc-600 hover:text-zinc-300 transition-colors shrink-0 mt-0.5"
                            >
                                <Cross2Icon className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                            {publishWinToast.score != null && (
                                <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${
                                    publishWinToast.score >= 80
                                        ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30'
                                        : publishWinToast.score >= 60
                                            ? 'text-amber-400 bg-amber-400/10 border-amber-400/30'
                                            : 'text-zinc-400 bg-zinc-400/10 border-zinc-400/30'
                                }`}>
                                    Quality: {publishWinToast.score}/100
                                </span>
                            )}
                            {publishWinToast.url && (
                                <a
                                    href={publishWinToast.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-auto flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                                >
                                    View Article →
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col px-7 py-6 gap-5">

                {/* Header row */}
                <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
                    <div>
                        <h1 className="text-xl font-bold text-zinc-100">Content Pipeline</h1>
                        <p className="text-xs text-zinc-500 mt-0.5">{topics.length} article{topics.length !== 1 ? 's' : ''} total</p>
                    </div>
                    <div className="flex-1" />

                    {/* Search */}
                    <div className="bg-zinc-900/60 border border-white/[0.07] rounded-xl p-0.5 flex items-center w-full sm:w-64">
                        <div className="pl-3 pr-2">
                            <MagnifyingGlassIcon className="w-4 h-4 text-violet-400" />
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search topics…"
                            className="flex-1 bg-transparent border-none h-8 text-sm text-white placeholder:text-zinc-500 focus:ring-0 focus:outline-none outline-none"
                        />
                    </div>

                    {/* Publish All Ready */}
                    {(() => {
                        const readyCount = topics.filter(t => PUBLISHABLE_STATUSES.includes(t.status)).length;
                        return readyCount > 0 ? (
                            <Button
                                variant="outline"
                                className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 gap-1.5 h-9 px-4 text-sm"
                                onClick={handlePublishAllReady}
                                disabled={bulkPublishing}
                            >
                                <PaperPlaneIcon className="h-3.5 w-3.5" />
                                Publish All Ready ({readyCount})
                            </Button>
                        ) : null;
                    })()}

                    <Button
                        className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 gap-1.5 h-9 px-4 text-sm font-semibold"
                        onClick={() => setShowAddPage(true)}
                    >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add Topic
                    </Button>
                </div>

                <div className="rounded-2xl border border-white/[0.07] bg-zinc-900/60 overflow-hidden flex-1 flex flex-col min-h-0">
                        <div className="overflow-y-auto flex-1 custom-scrollbar">
                            <Table>
                                <TableHeader className="sticky top-0 z-10 bg-zinc-950/90 backdrop-blur-md">
                                    <TableRow className="border-b border-white/[0.07] hover:bg-transparent">
                                        <TableHead className="w-10 h-10 px-4 text-left align-middle font-medium text-zinc-500">
                                            <Checkbox
                                                checked={selectedTopics.length === paginatedTopics.length && paginatedTopics.length > 0}
                                                onCheckedChange={toggleSelectAll}
                                                className="border-zinc-600"
                                            />
                                        </TableHead>
                                        <TableHead className="w-[35%] h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">Topic</TableHead>
                                        <TableHead className="w-[14%] h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">Site</TableHead>
                                        <TableHead className="w-14 h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">SEO</TableHead>
                                        <TableHead className="w-16 h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">Words</TableHead>
                                        <TableHead className="w-24 h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">Status</TableHead>
                                        <TableHead className="w-[14%] h-10 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-zinc-500">Progress</TableHead>
                                        <TableHead className="w-20 h-10 px-4 text-left align-middle font-medium text-muted-foreground">Date</TableHead>
                                        <TableHead className="w-32 h-10 px-4 text-right align-middle font-medium text-muted-foreground">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} className="text-center">
                                                <ReloadIcon className="h-6 w-6 animate-spin mx-auto mb-2" />
                                                Loading topics...
                                            </TableCell>
                                        </TableRow>
                                    ) : paginatedTopics.length > 0 ? (
                                        paginatedTopics.map((topic) => {
                                            const title = topic.generated_title || topic.topic_title || 'Untitled';
                                            const siteName = topic.word_press_site?.site_name || topic.wordpress_site?.name || 'No Site';
                                            const seoScore = topic.seo_score ?? null;
                                            const seoBreakdown = topic.seo_breakdown ?? null;
                                            const wordCount = formatWordCount(topic.word_count);

                                            return (
                                                <TableRow
                                                    key={topic.id}
                                                    className={`border-b hover:bg-muted/50 transition-colors ${selectedTopics.includes(topic.id) ? 'bg-muted' : ''}`}
                                                >
                                                    <TableCell className="px-4 py-2">
                                                        <Checkbox
                                                            checked={selectedTopics.includes(topic.id)}
                                                            onCheckedChange={() => toggleSelect(topic.id)}
                                                            className="border-zinc-600"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2 font-medium">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="truncate" title={title}>{title}</span>
                                                            {topic.status === 'published' && topic.wordpress_url && (
                                                                <a
                                                                    href={topic.wordpress_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="shrink-0 flex items-center gap-0.5 text-[10px] text-emerald-500 hover:text-emerald-400 transition-colors font-medium"
                                                                    title="View live post"
                                                                >
                                                                    <GlobeIcon className="h-3 w-3" />
                                                                    Live
                                                                </a>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2">
                                                        <div className="flex items-center gap-2 text-sm text-zinc-300 overflow-hidden">
                                                            <GlobeIcon className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                                                            <span className="truncate" title={siteName}>
                                                                {siteName}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2">
                                                        {renderSEOScore(seoScore, seoBreakdown)}
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2 text-zinc-400">
                                                        {wordCount}
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2">
                                                        {renderStatus(topic.status, topic.id)}
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2">
                                                        {renderStatusProgress(topic)}
                                                    </TableCell>
                                                    <TableCell className="px-4 py-2 text-zinc-500">
                                                        {formatDate(topic.created_at)}
                                                    </TableCell>
                                                    <TableCell className="text-right px-4 py-2">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700"
                                                                >
                                                                    <span className="text-lg leading-none">⋯</span>
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-700 text-zinc-200">
                                                                {(topic.status === 'draft' || topic.status === 'published') && topic.wordpress_url && (
                                                                    <DropdownMenuItem
                                                                        className="cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800 gap-2"
                                                                        onClick={() => topic.status === 'draft' ? handleViewDraft(topic.wordpress_url) : window.open(topic.wordpress_url, '_blank')}
                                                                    >
                                                                        <EyeOpenIcon className="h-4 w-4 text-blue-400" />
                                                                        <span>{topic.status === 'draft' ? 'Preview Draft' : 'View Live Post'}</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {topic.status === 'draft' && publishingId !== topic.id && (
                                                                    <DropdownMenuItem
                                                                        className="cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800 gap-2"
                                                                        onClick={() => handlePublishLive(topic.id)}
                                                                    >
                                                                        <PaperPlaneIcon className="h-4 w-4 text-green-400" />
                                                                        <span>Publish Live</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {topic.status === 'pending' && (
                                                                    <DropdownMenuItem
                                                                        className="cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800 gap-2"
                                                                        onClick={() => handleGenerate(topic.id)}
                                                                    >
                                                                        <LightningBoltIcon className="h-4 w-4 text-violet-400" />
                                                                        <span>Generate Article</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {topic.status === 'failed' && (
                                                                    <DropdownMenuItem
                                                                        className="cursor-pointer hover:bg-zinc-800 focus:bg-zinc-800 gap-2"
                                                                        disabled={retryingId === topic.id}
                                                                        onClick={() => handleRetry(topic.id)}
                                                                    >
                                                                        <ReloadIcon className={`h-4 w-4 text-orange-400 ${retryingId === topic.id ? 'animate-spin' : ''}`} />
                                                                        <span>{retryingId === topic.id ? 'Retrying…' : 'Retry Generation'}</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                <DropdownMenuSeparator className="bg-zinc-700" />
                                                                <DropdownMenuItem
                                                                    className="cursor-pointer hover:bg-red-900/30 focus:bg-red-900/30 gap-2 text-red-400 focus:text-red-400"
                                                                    onClick={() => handleDelete(topic.id)}
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                    <span>Delete</span>
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                {topics.length === 0 && !searchQuery && statusFilter.length === 0 && siteFilter.length === 0 ? (
                                                    /* Genuine empty state — zero articles ever */
                                                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                                                        <div className="p-4 rounded-full bg-violet-500/10">
                                                            <RocketIcon className="h-8 w-8 text-violet-400" />
                                                        </div>
                                                        <div className="text-center">
                                                            <h3 className="text-base font-semibold text-zinc-200">{emptyMsg.headline}</h3>
                                                            <p className="text-sm text-zinc-500 mt-1">{emptyMsg.body}</p>
                                                        </div>
                                                        {trendingTopics.length > 0 && (
                                                            <div className="flex flex-wrap gap-2 justify-center">
                                                                {trendingTopics.map((t, i) => (
                                                                    <button
                                                                        key={i}
                                                                        onClick={() => onGenerateClick && onGenerateClick(t.topic || t.title || t)}
                                                                        className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-zinc-300 hover:bg-violet-500/20 hover:border-violet-500/40 hover:text-violet-300 transition-all"
                                                                    >
                                                                        {t.topic || t.title || t}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button
                                                            onClick={() => onGenerateClick && onGenerateClick()}
                                                            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-medium text-sm shadow-md shadow-violet-500/25 transition-all duration-300 hover:scale-105 active:scale-95"
                                                        >
                                                            <LightningBoltIcon className="h-4 w-4 text-yellow-400" />
                                                            Generate Your First Article
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-6 text-zinc-500 text-sm">
                                                        No topics match your current filters
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center border-t border-white/5 px-4 py-3 bg-white/5 gap-4">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sm text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                >
                                    Previous
                                </Button>
                                <span className="text-sm text-zinc-500">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sm text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        )}

                        {bulkPublishToast && (
                            <div className="absolute top-4 right-4 z-30 bg-zinc-900 border border-white/10 rounded-lg px-4 py-3 shadow-xl text-sm flex items-center gap-3">
                                <PaperPlaneIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                <span className="text-zinc-200">
                                    {bulkPublishToast.published} published
                                    {bulkPublishToast.failed > 0 ? `, ${bulkPublishToast.failed} failed` : ''}
                                </span>
                            </div>
                        )}

                        {selectedTopics.length > 0 && (
                            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
                                <div className="bg-zinc-950/90 backdrop-blur-xl border border-white/[0.12] rounded-full px-6 py-3 shadow-2xl shadow-black/50 flex items-center gap-4">
                                    <span className="text-sm text-zinc-300 font-medium">
                                        {selectedTopics.length} Selected
                                    </span>
                                    <div className="h-4 w-px bg-white/20" />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-3 text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
                                        onClick={handleBulkGenerate}
                                    >
                                        <LightningBoltIcon className="h-3.5 w-3.5 mr-1.5" />
                                        Regenerate
                                    </Button>
                                    {selectedTopics.some(id => {
                                        const t = topics.find(tp => tp.id === id);
                                        return t && PUBLISHABLE_STATUSES.includes(t.status);
                                    }) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 px-3 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                            onClick={handleBulkPublish}
                                            disabled={bulkPublishing}
                                        >
                                            <PaperPlaneIcon className="h-3.5 w-3.5 mr-1.5" />
                                            Publish
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-3 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                        onClick={openBulkDeleteModal}
                                    >
                                        <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
            </div>

            {showAddPage && (
                <QuickPost
                    user={{}}
                    onClose={(shouldReload) => {
                        setShowAddPage(false);
                        if (shouldReload === 'topics') {
                            loadTopics();
                        }
                    }}
                    onSuccess={() => {
                        setShowAddPage(false);
                        loadTopics();
                    }}
                />
            )}

            {showDeleteModal && selectedTopicForDelete && (
                <DeleteModal
                    topic={selectedTopicForDelete}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedTopicForDelete(null);
                    }}
                    onDelete={handleDeleteSuccess}
                />
            )}

            {showBulkDeleteModal && (
                <BulkDeleteModal
                    count={selectedTopics.length}
                    loading={loading}
                    onClose={() => setShowBulkDeleteModal(false)}
                    onConfirm={confirmBulkDelete}
                />
            )}

            {showDraftPreviewDialog && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowDraftPreviewDialog(false)}>
                    <div className="bg-zinc-900 border border-white/10 rounded-xl max-w-md w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-white/10">
                            <h3 className="text-lg font-semibold text-zinc-100">📝 Viewing WordPress Drafts</h3>
                        </div>
                        <div className="px-6 py-4 text-sm text-zinc-300 space-y-3">
                            <p>To preview this draft, you need to be logged into your WordPress site as an administrator.</p>
                            <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3 space-y-1.5">
                                <p className="flex items-center gap-2">
                                    <CheckIcon className="h-4 w-4 text-violet-400" />
                                    Make sure you're logged in to WordPress
                                </p>
                                <p className="flex items-center gap-2">
                                    <CheckIcon className="h-4 w-4 text-violet-400" />
                                    The draft will open in a new tab
                                </p>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="dontShowAgain"
                                    className="w-4 h-4 rounded border-zinc-600 text-violet-600 focus:ring-violet-500"
                                />
                                <span className="text-zinc-400 text-sm">Don't show this again</span>
                            </label>
                        </div>
                        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
                            <Button
                                variant="ghost"
                                className="text-zinc-400 hover:text-zinc-200"
                                onClick={() => setShowDraftPreviewDialog(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                                onClick={() => {
                                    const dontShowAgain = document.getElementById('dontShowAgain').checked;
                                    handleDraftPreviewConfirm(dontShowAgain);
                                }}
                            >
                                Continue to Draft
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TopicQueue;
